/**
 * Petit champ de recherche d'équipe (autocomplete texte → id).
 *
 * Utilisé pour choisir un adversaire (proposer un défi) ou filtrer un
 * historique de défis par équipe.
 *
 * UX : chaque résultat affiche l'avatar de l'équipe et son capitaine, pour
 * distinguer facilement des équipes de nom proche (la division est indiquée
 * à la place de l'effectif lorsque l'équipe est inscrite au championnat en
 * cours). Une fois une équipe choisie, le champ de recherche est masqué au
 * profit d'une carte de confirmation compacte (avatar + capitaine + division
 * ou effectif, cliquable pour ouvrir la fiche de l'équipe) avec un bouton pour
 * annuler le choix et revenir à la recherche. Les deux états partagent la même
 * largeur/hauteur (PICKER_WIDTH/PICKER_HEIGHT) pour éviter que la sélection ne
 * fasse « bouger » le reste du formulaire.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { PlayerAvatar } from '../PlayerAvatar';
import { useProfile } from '../../contexts/ProfileContext';
import { searchTeams, type TeamSearchItem } from '../../lib/teamsApi';
import { CloseIcon } from '../Icons';
import { BACKEND_URL } from '../../config';

interface TeamPickerProps {
  value: { id: number; nom: string } | null;
  onChange: (team: { id: number; nom: string } | null) => void;
  placeholder?: string;
  excludeTeamId?: number;
}

function teamAvatarUrl(nom: string, size = 64): string {
  return `${BACKEND_URL}/api/avatars/team/${encodeURIComponent(nom)}?size=${size}`;
}

// Largeur/hauteur fixes communes aux deux états (recherche / équipe choisie) pour
// éviter que la sélection d'une équipe adverse ne fasse « bouger » le formulaire.
const PICKER_WIDTH = 220;
const PICKER_HEIGHT = 48;

/** Premier membre de la liste ordonnée (capitaine, lieutenant, puis postes), pseudo décodé pour l'affichage */
function captainOf(team: TeamSearchItem): string | null {
  return team.membersDisplay?.split(',')[0]?.trim() || null;
}

/** Indication d'effectif : division au championnat si inscrite, sinon nombre de joueurs */
function effectifOrDivision(team: TeamSearchItem): string {
  if (team.division) return team.division.nom;
  return `${team.effectif}/8`;
}

export default function TeamPicker({ value, onChange, placeholder = 'Rechercher une équipe…', excludeTeamId }: TeamPickerProps) {
  const { openTeamProfile } = useProfile();
  const [query, setQuery] = useState(value?.nom || '');
  const [results, setResults] = useState<TeamSearchItem[]>([]);
  const [selectedInfo, setSelectedInfo] = useState<TeamSearchItem | null>(null);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setQuery(value?.nom || ''); if (!value) setSelectedInfo(null); }, [value]);

  const runSearch = useCallback((q: string) => {
    if (q.trim().length < 1) { setResults([]); return; }
    searchTeams({ search: q, limit: 10 })
      .then((data) => {
        setResults(data.teams.filter((t) => t.id !== excludeTeamId));
        // Une recherche qui aboutit doit rouvrir la liste même si le focus a été
        // perdu/regagné entre-temps sans déclencher onFocus (ex: certains outils
        // d'automatisation, ou re-focus programmatique) : on affiche le résultat
        // dès qu'il y a une requête en cours, indépendamment de l'état de focus.
        setOpen(true);
      })
      .catch(() => setResults([]));
  }, [excludeTeamId]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, runSearch]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const select = (t: TeamSearchItem) => {
    onChange({ id: t.id, nom: t.nom });
    setSelectedInfo(t);
    setQuery('');
    setOpen(false);
  };

  const cancelSelection = () => {
    onChange(null);
    setSelectedInfo(null);
    setQuery('');
  };

  const captainDisplay = selectedInfo ? captainOf(selectedInfo) : null;

  if (value) {
    return (
      <div style={{ width: PICKER_WIDTH, height: PICKER_HEIGHT }}>
        <div
          role="button"
          tabIndex={0}
          title={selectedInfo ? 'Voir la fiche de l\'équipe' : undefined}
          onClick={() => selectedInfo && openTeamProfile(value.id)}
          onKeyDown={(e) => { if (selectedInfo && (e.key === 'Enter' || e.key === ' ')) openTeamProfile(value.id); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', height: '100%', boxSizing: 'border-box',
            borderRadius: 8, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
            cursor: selectedInfo ? 'pointer' : 'default',
          }}
        >
          <PlayerAvatar pseudo={value.nom} size={28} photoUrl={teamAvatarUrl(value.nom, 56)} />
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value.nom}</span>
            {selectedInfo && (
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {captainDisplay || '—'} · {effectifOrDivision(selectedInfo)}
              </span>
            )}
          </div>
          <button
            type="button"
            title="Annuler la sélection"
            onClick={(e) => { e.stopPropagation(); cancelSelection(); }}
            className="cia-btn cia-btn--icon cia-btn--small"
          >
            <CloseIcon size={13} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} style={{ position: 'relative', width: PICKER_WIDTH, height: PICKER_HEIGHT }}>
      <input
        type="text"
        placeholder={placeholder}
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        style={{
          width: '100%', height: '100%', padding: '6px 10px', borderRadius: 8, boxSizing: 'border-box',
          border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)',
        }}
      />

      {open && results.length > 0 && (
        <div
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
            background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
            borderRadius: 8, marginTop: 4, maxHeight: 280, overflowY: 'auto',
            boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
          }}
        >
          {results.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => select(t)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '8px 10px',
                border: 'none', background: 'none', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.85rem',
              }}
              onMouseDown={(e) => e.preventDefault()}
            >
              <PlayerAvatar pseudo={t.nom} size={28} photoUrl={teamAvatarUrl(t.nom, 56)} />
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.nom}</span>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.72rem' }}>
                  Capitaine : {captainOf(t) || '—'} · {effectifOrDivision(t)}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
